import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  ProgressStatusResponse,
  getOfferContent,
  initiateOfferContentGeneration,
} from '~/services/AccommodationService';
import { HttpResponse } from '~/services/types';

export const offerContentQueryKeys = {
  all: ['offerContent'] as const,
  detail: (offerId: string | undefined) => [...offerContentQueryKeys.all, 'detail', offerId] as const,
};

interface UseGetOfferContentParams {
  offerId: string | undefined;
  enabled?: boolean;
}

export function useGetOfferContent({ offerId, enabled = true }: UseGetOfferContentParams) {
  return useQuery({
    queryKey: offerContentQueryKeys.detail(offerId),
    queryFn: async ({ signal }) => {
      const response = await getOfferContent({ offerId: offerId ?? '' }, { signal });
      return response.result;
    },
    enabled: enabled && !!offerId,
  });
}

export interface InitiateOfferContentGenerationParams {
  contentType?: 'description' | 'nearby' | 'finePrint';
  includeOfferInGeneration?: boolean;
  useWebSearch: boolean;
}

export function useInitiateOfferContentGeneration(offerId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation<HttpResponse<ProgressStatusResponse>, Error, InitiateOfferContentGenerationParams>({
    mutationFn: (params) =>
      initiateOfferContentGeneration({
        offerId: offerId ?? '',
        ...params,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: offerContentQueryKeys.detail(offerId) });
    },
  });
}
